import React, { useContext } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import {
  Bars3Icon,
  XMarkIcon,
} from '@heroicons/react/24/solid';
import { ReactComponent as SmashLogo } from '../../assets/smash_logo.svg';
import ProfileDropdown from './ProfileDropdown';
import { UserContext } from '../../utils/contexts/UserContext';
import DarkModeToggle from './DarkModeToggle';
import { getNavigationItems } from './navigationConfig';

const Navbar = ({ menuHidden = false, disableNav = false }) => {
  const navigate = useNavigate();
  const { user, isStudent, isExpert, isUniversity, isAdmin } = useContext(UserContext);
  const navItems = getNavigationItems({ isStudent, isExpert, isUniversity, isAdmin });

  const NavItem = ({ item, mobile = false }) => (
    <NavLink
      to={item.path}
      className={({ isActive, isPending }) =>
        `${
          mobile
            ? 'block px-3 py-2 text-base'
            : 'flex flex-col justify-center items-center text-sm cursor-pointer gap-2'
        }
         ${
           isActive && item.path !== '#'
             ? 'text-blue-500 dark:text-blue-200 font-semibold'
             : 'text-gray-800 dark:text-gray-100 font-medium'
         }
         ${isPending ? 'opacity-50 animate-pulse' : ''}`
      }
    >
      {({ isActive, isPending }) => (
        <div
          className={`${
            mobile
              ? ''
              : 'flex flex-row items-center p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-300'
          }
           ${isPending ? 'scale-95' : ''}
         `}
        >
          <item.icon className="mr-2 h-5 w-5" />
          <p>
            {item.label}
            {isPending && <span className="ml-2 text-xs">...</span>}
          </p>
        </div>
      )}
    </NavLink>
  );

  return (
    <Disclosure
      as="nav"
      className={`bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 w-full z-50 ${
        disableNav ? 'pointer-events-none' : ''
      }`}
    >
      {({ open }) => (
        <>
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-14">
              {/* Logo */}
              <div className="flex items-center">
                <div className="flex-shrink-0 flex items-center">
                  <SmashLogo
                    className="w-20 h-auto cursor-pointer"
                    onClick={() => navigate('/')}
                  />
                </div>
              </div>

              {/* Desktop Navigation - Centered */}
              <div className="hidden md:flex flex-1 justify-center">
                <div className="flex gap-1 lg:gap-5">
                  {!menuHidden &&
                    navItems.map((item) => <NavItem key={item.path} item={item} />)}
                </div>
              </div>

              {/* Right side - Desktop */}
              <div className="hidden md:flex flex-row justify-center items-center gap-4">
                <DarkModeToggle />
                <ProfileDropdown userData={user} />
              </div>

              {/* Mobile menu button */}
              <div className="md:hidden flex items-center gap-4">
                <DarkModeToggle />
                <ProfileDropdown userData={user} />
                <DisclosureButton className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </DisclosureButton>
              </div>
            </div>
          </div>

          {/* Mobile menu */}
          <DisclosurePanel className="md:hidden">
            <div className="p-2 space-y-1 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
              {!menuHidden &&
                navItems.map((item) => (
                  <NavItem key={item.path} item={item} mobile />
                ))}
            </div>
          </DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
};

export default Navbar;
